// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { CollectRouteSiteInfo } from 'types/routeManagementTypes';
import styles from './CollectRouteSelectStep.module.scss';
import { getApiPath, mergeClasses, normalizeNumber } from 'util/commons';
import { CollectAreaStatusIcon } from '../CollectAreaStatusIcon';
import IconMemo from 'components/atoms/icons/IconMemo';
import Popover from 'components/atoms/Popover';
import { IconArrowRight } from 'components/atoms/icons/IconArrowRight';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import { useHistory, useParams } from 'react-router';
import { Path } from 'routes';
import { useTranslation } from 'react-i18next';
import { SiteMemo } from '../SiteMemo';
import { PrintLabel } from 'util/Enums';
import { useLayoutEffect, useRef } from 'react';

type CollectRouteSelectStepProps = {
  sites?: CollectRouteSiteInfo[];
  activeId?: string;
  className?: string;
  processedSiteIds?: string[];
  mode?: 'filtered' | 'all';
  onMemoUpdateSuccess?: (siteId: string, note?: string) => void;
};

const CollectRouteSelectStep: React.FC<CollectRouteSelectStepProps> = ({
  sites,
  activeId,
  className,
  processedSiteIds,
  mode = 'filtered',
  onMemoUpdateSuccess,
}) => {
  const history = useHistory();
  const { routeId: rawRouteId } = useParams<{ routeId: string }>();
  const routeId = mode === 'filtered' ? rawRouteId : 'none';
  const { t } = useTranslation();
  const activeRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeId]);

  const renderSteppedSite = (data: CollectRouteSiteInfo) => {
    const isProcessed = processedSiteIds?.includes(data.id);

    const isDisabled =
      isProcessed ||
      (!data.enableSubstituteRegist &&
        (data.weight === '0' ||
          (!data.collectRequestFlg && !data.emissionFlg)));

    const isActive = data.id === activeId;

    const handleClickSite = () => {
      let url = getApiPath(Path.collectItemInSite, {
        routeId,
        siteId: data.id,
      });

      if (data.printLabel === PrintLabel.QRCode) {
        url = getApiPath(Path.routeSiteQRScanning, {
          routeId,
          siteId: data.id,
        });
      }

      if (data.enableSubstituteRegist) {
        url = getApiPath(Path.substituteRegistration, {
          routeId,
          siteId: data.id,
        });
      }

      history.push(
        `${url}${window.location.search}`,
        mode === 'all' && {
          routeId: rawRouteId,
          refreshSites: true,
        }
      );
    };

    return (
      <div
        className={mergeClasses(styles.steppedSite, {
          [styles.steppedSiteActive]: isActive,
          [styles.steppedSiteProcessed]: isDisabled,
        })}
        key={data.id}
        onClick={handleClickSite}
      >
        <div className={styles.stepOverview}>
          <div className={styles.stepHeader}>
            {isProcessed && <CollectAreaStatusIcon collectedFlg />}

            <CollectAreaStatusIcon
              collectRequestFlg={data.weight !== '0' && data.collectRequestFlg}
              emissionFlg={data.weight !== '0' && data.emissionFlg}
              substituteFlg={data.enableSubstituteRegist}
            />

            {data.hasMemo && (
              <SiteMemo
                siteId={data.id}
                siteName={data.name}
                hasMemo={data.hasMemo}
                onMemoUpdateSuccess={(note) =>
                  onMemoUpdateSuccess?.(data.id, note)
                }
                renderer={({ handleOpen }) => (
                  <CollectAreaStatusIcon
                    hasMemo
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen();
                    }}
                  />
                )}
              />
            )}
          </div>

          <div className={styles.stepBody}>
            <div className={styles.siteInfo}>
              <div>
                <Popover
                  autoGen={{
                    odd: 4,
                    wrapperStyle: {
                      width: 'fit-content',
                    },
                  }}
                  popUpStyle={{
                    fontSize: 18,
                  }}
                  positions={['top']}
                  content={data.name}
                >
                  <span className={styles.siteName}>{data.name}</span>
                </Popover>

                <Popover
                  autoGen={{
                    odd: 0,
                    lines: 2,
                    wrapperStyle: {
                      width: 'fit-content',
                    },
                  }}
                  popUpStyle={{
                    fontSize: 14,
                  }}
                  positions={['top']}
                  content={data.address}
                >
                  <span className={styles.siteAddress}>{data.address}</span>
                </Popover>
              </div>

              {!data.enableSubstituteRegist && (
                <div className={styles.siteStats}>
                  <div className={styles.siteStatItem}>
                    <p className={styles.siteStatLabel}>
                      {t('confirm_visit_list.overall.quantity')}
                    </p>

                    <Popover
                      autoGen={{
                        odd: 10,
                        wrapperStyle: {
                          width: 'fit-content',
                        },
                      }}
                      popUpStyle={{
                        fontSize: 14,
                      }}
                      positions={['top']}
                      content={`${normalizeNumber({
                        value: data.quantity,
                      })} ${t('confirm_visit_list.overall.quantity_unit')}`}
                    >
                      <span className={styles.siteStatValue}>
                        {normalizeNumber({
                          value: data.quantity || 0,
                        })}
                        <span className={styles.unit}>
                          {t('confirm_visit_list.overall.quantity_unit')}
                        </span>
                      </span>
                    </Popover>
                  </div>

                  <div className={styles.siteStatItem}>
                    <p className={styles.siteStatLabel}>
                      {' '}
                      {t('confirm_visit_list.overall.weight')}
                    </p>

                    <Popover
                      autoGen={{
                        odd: 10,
                        wrapperStyle: {
                          width: 'fit-content',
                        },
                      }}
                      popUpStyle={{
                        fontSize: 14,
                      }}
                      positions={['top']}
                      content={`${normalizeNumber({
                        value: data.weight,
                      })} kg`}
                    >
                      <span
                        className={mergeClasses(
                          styles.siteStatValue,
                          styles.weightValue
                        )}
                      >
                        {normalizeNumber({
                          value: data.weight || 0,
                        })}
                        <span className={styles.unit}>kg</span>
                      </span>
                    </Popover>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.stepActionWrapper}>
          <SiteMemo
            siteId={data.id}
            siteName={data.name}
            hasMemo={data.hasMemo}
            onMemoUpdateSuccess={(note) => onMemoUpdateSuccess?.(data.id, note)}
            renderer={({ handleOpen }) => (
              <div
                className={mergeClasses(styles.siteAction, styles.memoAction, {
                  [styles.memoDisabled]: isDisabled,
                })}
                onClick={(e) => {
                  e.stopPropagation();

                  if (!isDisabled) {
                    handleOpen();
                  }
                }}
              >
                <IconMemo color={!isDisabled ? '#86ea5c' : undefined} />
              </div>
            )}
          />

          <div
            className={mergeClasses(styles.siteAction, styles.selectAction, {
              [styles.selectActive]: isActive,
              [styles.selectDisabled]: isDisabled,
            })}
          >
            {!isProcessed ? (
              <IconArrowRight
                color={isActive ? 'black' : isDisabled ? undefined : '#86ea5c'}
                size={28}
              />
            ) : (
              <IconCheck size={28} fillColor="#6F6F6F" borderColor="#6F6F6F" />
            )}
          </div>
        </div>

        {isActive && (
          <div className={styles.nextTag} ref={activeRef}>
            <IconArrowRight color="black" size={16} />
            <span>{t('common.button.next')}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={mergeClasses(styles.stepsContainer, className)}>
      {sites?.map((site) => renderSteppedSite(site))}
    </div>
  );
};

export default CollectRouteSelectStep;
